import { BehaviorSubject, EMPTY } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import AddAdditionalFilesDialog from '../components/additionalFiles/AddAdditionalFilesDialog';
import ListAdditionalFilesDialog from '../components/additionalFiles/ListAdditionalFilesDialog';
import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import ImageWrapper from '../components/common/ImageWrapper';
import VideoWrapper from '../components/common/VideoWrapper';
import RemoveImagesDialog from '../components/dashboardStudyList/RemoveImagesDialog';
import DashboardTourInitModal from '../components/dashboardTour/DashboardTourInitModal';
import DownloadList from '../components/downloadList/DownloadList';
import ObjViewer from '../components/objViewer/ObjViewer';
import PatientShareVideoReportPopup from '../components/patientShareVideoReportPopup/PatientShareVideoReportPopup';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import UploadProgress from '../containers/upload/UploadProgress';
import PatientRecord from '../models/PatientRecord';
import PatientRecordDetails from '../models/PatientRecordDetails';
import { DCM, DOCS, IMAGE, VIDEO, THREE } from '../models/Resource';
import { WorkspaceProductType } from '../models/Workspace';
import parseFiles from '../utils/dicomUtils';

const defaultCriteria = {
  keyword: '',
  limit: 10,
  orderField: null,
  orderType: 'ASC',
  page: 0,
};

export default class PatientDashboardBloc {
  records$ = new BehaviorSubject([]);
  total$ = new BehaviorSubject(0);
  totalAvailableCases$ = new BehaviorSubject(0);
  searchCriteria$ = new BehaviorSubject(defaultCriteria);
  selectedRecord$ = new BehaviorSubject(null);
  hasRecords$ = new BehaviorSubject(null);
  isSubmitting$ = new BehaviorSubject(false);
  isLoadingStudyDetails$ = new BehaviorSubject(false);
  runJoyride$ = new BehaviorSubject(false);
  isMobileDialogDetailOpen$ = new BehaviorSubject(false);
  updatedAdditionalFile$ = new BehaviorSubject({});
  shouldReopenAdditionalFilesDialog$ = new BehaviorSubject(false);
  subscribedCaseId$ = new BehaviorSubject([]);
  caseIdsSubscription$ = new BehaviorSubject([]);
  shouldRedirectToPackagePlans$ = new BehaviorSubject(false);
  subscribedImageStoreSuccess$ = new BehaviorSubject(false);
  imageStoreSubscription$ = new BehaviorSubject(null);

  constructor({
    appRepository,
    patientRepository,
    userRepository,
    dialogRepository,
    casesRepository,
    authRepository,
    errorRepository,
    uploadProgressRepository,
    notificationsRepository,
    resourceRepository,
  }) {
    this.appRepository = appRepository;
    this.patientRepository = patientRepository;
    this.userRepository = userRepository;
    this.dialogRepository = dialogRepository;
    this.casesRepository = casesRepository;
    this.errorRepository = errorRepository;
    this.authRepository = authRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.notificationsRepository = notificationsRepository;
    this.resourceRepository = resourceRepository;
    this.searchSubscription = this.searchCriteria$.subscribe((criteria) => {
      this.getRecords();
    });
  }

  dispose() {
    this.searchSubscription.unsubscribe();
    this.removeCaseIdSubscription();
    this.removeImageStoreSubscription();
  }

  updateSearchCriteria(updatedCriteria) {
    const nextSearchCriteria = {
      ...this.searchCriteria$.getValue(),
      ...updatedCriteria,
    };

    if (updatedCriteria.keyword) nextSearchCriteria.page = 0;

    this.searchCriteria$.next(nextSearchCriteria);
  }

  getCurrentUser() {
    return this.userRepository.getCurrentUser();
  }

  async getNotPaidCases() {
    const workspaceDetails = this.userWorkspace$.getValue();
    if (workspaceDetails?.canRequestBeforePay) await this.casesRepository.getNotPaidCases();
  }

  /**
   * Get all records. If removedId exists, check if it still exists in the list of studies to reselect it.
   * Otherwise, select the first record.
   * @param removedId
   * @returns {Promise<void>}
   */
  async getRecords(removedId = null, removedIndex = null) {
    const searchCriteria = this.searchCriteria$.getValue();
    const previousRecords = this.records$.getValue();
    const cases = await this.patientRepository.getPatientRecords(searchCriteria);
    cases.data = cases.data.map((item) => new PatientRecord(item));

    this.records$.next(cases.data);
    this.total$.next(cases.total);

    // Set total available cases
    if (!searchCriteria.keyword) {
      this.totalAvailableCases$.next(cases.total);
    }

    // Set first selected record
    if (!previousRecords.length && cases.data.length) {
      this.getSelectedRecord(cases.data[0]?.id);
    }

    this.handleRemovedRecordDetail(removedId, removedIndex);
  }

  handleRemovedRecordDetail(removedId = null, removedIndex = null) {
    if (!removedId) return;

    const selectedRecord = this.selectedRecord$.getValue();
    if (selectedRecord?.id !== removedId) return;

    const records = this.records$.getValue();
    const recordsLength = records.length;

    // If image only
    if (!selectedRecord?.case) {
      // Set selectedRecord to null if no records available
      if (!records?.length) {
        this.selectedRecord$.next(null);
        return;
      }

      // Set selectedRecord to the current index if next records available
      if (removedIndex < recordsLength) {
        this.getSelectedRecord(records[removedIndex].id);
        return;
      }

      // Set selectedRecord to the last index if next records not available
      this.getSelectedRecord(records[recordsLength - 1].id);
      return;
    }

    // If this data is a case, get the selected record again
    this.getSelectedRecord(removedId);
  }

  async getSelectedRecord(id) {
    this.isLoadingStudyDetails$.next(true);
    this.openMobileDialogDetail();

    const record = await this.patientRepository.getRecord(id);
    const records = this.records$.getValue();

    const frameUrl = record.caseId ? await this.casesRepository.getLinkViewer(record.caseId) : await this.patientRepository.getViewerLink(record.id);
    const { viewerLink } = frameUrl;

    const selectedRecord = records.filter((rcrd) => rcrd.id === id);
    const patientRecordDetail = new PatientRecordDetails({ ...(selectedRecord[0] || {}), ...record, viewerLink });

    this.selectedRecord$.next(patientRecordDetail);
    this.isLoadingStudyDetails$.next(false);
    this.shouldRedirectToPackagePlans$.next(false);
  }

  get planDetails$() {
    return this.userRepository.userDetails$.pipe(
      map((details) => {
        return {
          planType: details?.userPlan?.name,
          totalStudies: details?.userPlan?.maxStudy,
          uploadedStudies: details?.userPlan?.studyCount,
        };
      })
    );
  }

  get availableReport$() {
    return this.userRepository.userDetails$.pipe(
      map((details) => {
        return details.availableReport || null;
      })
    );
  }

  get notPaidCasesTotal$() {
    return this.casesRepository.notPaidCasesTotal$;
  }

  async onUpload(files, fileType = DCM) {
    try {
      this.isSubmitting$.next(true);
      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      let payload;

      if (fileType === DCM) {
        const status = await parseFiles(files);
        const parsedFiles = [...status.files];
        if (status.uploadedReport) {
          parsedFiles.push(status.uploadedReport);
        }
        payload = {
          fileType,
          files: parsedFiles,
          title: status.study,
        };
      } else {
        payload = {
          fileType,
          files,
          title: files[0].name || '',
        };
      }

      await this.patientRepository.addStudy(payload, this.uploadProgressRepository.progress$);

      this.userRepository.removeIsPatientDemoAccount();
      this.userRepository.removeIsPatientDashboardTourShown();

      this.selectedRecord$.next(null);
      this.updateSearchCriteria({ isSample: undefined });

      if (!this.subscribedImageStoreSuccess$.getValue()) this.imageStoreSubscription$.next(this.listenToStoreImageUpdate().subscribe());

      // updating plan details
      await this.userRepository.getCurrentUser();
      this.searchCriteria$.next(defaultCriteria);
      this.errorRepository.error$.next({
        message: 'Your files are being processed. Once this is completed you will be notified by email.',
        severity: 'success',
      });

      this.uploadProgressRepository.finish();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message,
        severity: 'error',
      });
      this.uploadProgressRepository.fail();
    } finally {
      this.isSubmitting$.next(false);
    }
  }

  async downloadFiles() {
    try {
      this.appRepository.handleOpenLoading();

      const files = await this.patientRepository.downloadFiles(this.selectedRecord$.getValue().id);
      const fileType = this.selectedRecord$.getValue()?.type;
      let childResources = [];

      if (fileType === DCM || ![VIDEO, IMAGE, THREE, 'pdf', DOCS].includes(fileType)) {
        childResources = await this.patientRepository.getChildResourcesLink(this.selectedRecord$.getValue().id);
      }

      if (childResources?.length) {
        const downloadLinks = await Promise.all(childResources.map((resource) => this.patientRepository.downloadFiles(resource.id)));
        const managedResources = childResources.map((resource, idx) => {
          return { link: downloadLinks[idx]?.downloadLink, title: resource.description };
        });

        this.dialogRepository.loadDialog(DownloadList, {
          linkResources: [{ link: files?.downloadLink, title: this.selectedRecord$.getValue().description }, ...managedResources],
        });
        return;
      }

      window.open(files.downloadLink, '_blank');
    } finally {
      this.appRepository.handleCloseLoading();
    }
  }

  async removeFiles() {
    const selectedRecord = this.selectedRecord$.getValue();
    const records = this.records$.getValue();

    const selectedRecordIndex = records.findIndex((record) => record.id === selectedRecord.id);

    await this.patientRepository.removeFiles(selectedRecord.id);
    this.selectedRecord$.next(null);

    await this.userRepository.getCurrentUser();
    await this.getRecords(selectedRecord.id, selectedRecordIndex);
  }

  openSelectImagingStudyDialogSelector() {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      confirmateUpload: true,
      dialogTitle: 'Select Files',
      onCrossIconClick: () => this.closeDialog(),
      onUpload: (data) => {
        this.closeDialog();
        if (data.type === 'dcm') {
          this.onUpload(data.files);
          return;
        }

        const fileType = data.type === 'document' ? 'pdf' : data.type;

        this.onUpload(data.files, fileType);
      },
    });
  }

  async openRemoveImagesDialog() {
    this.dialogRepository.loadDialog(RemoveImagesDialog, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onCloseIconClick: () => this.dialogRepository.toggleDialog(false),
      onConfirm: () => {
        this.removeFiles();
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async viewSelectedImages() {
    try {
      this.appRepository.handleOpenLoading();

      const selectedRecord = this.selectedRecord$.getValue();
      const { viewerLink } = await this.patientRepository.getViewerLink(selectedRecord.id);
      let childResources = [];

      const title = selectedRecord?.description || selectedRecord?.title;

      const fileType = selectedRecord?.type;
      if (fileType === DCM || ![VIDEO, IMAGE, THREE, 'pdf', DOCS].includes(fileType)) {
        childResources = (await this.patientRepository.getChildResourcesLink(selectedRecord.id)) || [];
      }

      if (fileType === VIDEO) {
        this.dialogRepository.loadDialog(VideoWrapper, { isPlayOnClick: true, url: viewerLink });
      } else if (fileType === IMAGE) {
        this.dialogRepository.loadDialog(ImageWrapper, { url: viewerLink });
      } else if (fileType === THREE) {
        this.dialogRepository.loadDialog(ObjViewer, { url: viewerLink });
      } else if (fileType === 'pdf' || fileType === DOCS) {
        this.dialogRepository.loadDialog(PdfViewer, { url: viewerLink });
      } else {
        this.dialogRepository.loadDialog(AmbraContainer, {
          childResources,
          currentRecord: selectedRecord,
          onClose: () => this.dialogRepository.toggleDialog(false),
          title,
          url: viewerLink,
        });
      }
    } finally {
      this.appRepository.handleCloseLoading();
    }
  }

  get isMediphanyWorkspace$() {
    return this.authRepository.userWorkspace$.pipe(map((workspace) => workspace.isMediphanyWorkspace));
  }

  get isWhiteGloveWorkspace$() {
    return this.authRepository.userWorkspace$.pipe(map((workspace) => workspace.productType === WorkspaceProductType.whiteGlove.value));
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  get userDetails$() {
    return this.userRepository.userDetails$.pipe(
      map((userDetail) => userDetail),
      tap((userDetail) => {
        const isPatientDemoAccount = this.userRepository.getIsPatientDemoAccount();
        const isDemo = userDetail?.firstOrderStatus === 'demo' || !!isPatientDemoAccount;
        const shouldFetchSampleRecords = userDetail?.firstOrderStatus === 'demo' || userDetail?.firstOrderStatus === 'registered';

        const searchCriteria = this.searchCriteria$.getValue();
        if (isDemo && shouldFetchSampleRecords) {
          this.showTourDialog();
          this.updateSearchCriteria({ isSample: true });
        } else if (searchCriteria.isSample) {
          this.updateSearchCriteria({ isSample: undefined });
        }
      })
    );
  }

  async getShareLink() {
    this.dialogRepository.loadDialog(PatientShareVideoReportPopup, {
      resourceId: this.selectedRecord$.getValue().id,
      type: 'viewer-link',
    });
  }

  async getShareVideoReport(studyId) {
    this.dialogRepository.loadDialog(PatientShareVideoReportPopup, {
      resourceId: studyId,
      type: 'video-report',
    });
  }

  showTourDialog() {
    const isPatientDashboardTourShown = this.userRepository.getIsPatientDashboardTourShown();
    if (isPatientDashboardTourShown) return;

    this.userRepository.setIsPatientDashboardTourShown(1);
    this.dialogRepository.loadDialog(DashboardTourInitModal, {
      onCrossIconClick: () => this.closeDialog(),
      onStartProductDemo: () => this.startProductDemo(),
    });
  }

  startProductDemo() {
    this.runJoyride$.next(true);
    this.closeDialog();
    this.closeMobileDialogDetail();
  }

  closeJoyride() {
    this.runJoyride$.next(false);
    this.openMobileDialogDetail();
  }

  closeDialog() {
    this.dialogRepository.toggleDialog(false);
  }

  openMobileDialogDetail() {
    this.isMobileDialogDetailOpen$.next(true);
  }

  closeMobileDialogDetail() {
    this.isMobileDialogDetailOpen$.next(false);
  }

  openSelfHelpMaterialTour() {
    this.userRepository.setShouldRunSelfHelpJoyride(1);
  }

  openAdditionalFileDialog(allowDocxFiles = false) {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      allowDocxFiles,
      onCrossIconClick: () => this.closeDialog(),
      onUpload: async (data) => {
        this.closeDialog();
        try {
          await this.uploadAdditionalFile(data);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('Additional File Dialog Error:', error);
        }
      },
    });
  }

  async uploadAdditionalFile(data) {
    const { files, originalType, type } = data;
    if (!files) return;

    const selectedRecord = this.selectedRecord$.getValue();
    const payload = { files, originalType, type };

    if (!selectedRecord?.caseId) return;
    payload.caseId = selectedRecord.caseId;

    try {
      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      await this.casesRepository.addAdditonalFile(payload, this.uploadProgressRepository.progress$);

      this.uploadProgressRepository.finish();

      this.errorRepository.error$.next({
        message: 'Additional file is being processed and will be available in a few minutes',
        severity: 'success',
      });

      this.manageCaseIdSubscription(selectedRecord.caseId);
    } catch (error) {
      this.uploadProgressRepository.fail();
      throw error;
    }
  }

  manageCaseIdSubscription(caseId) {
    const subscribedCaseIds = this.subscribedCaseId$.getValue();
    if (subscribedCaseIds.includes(caseId)) return;

    const subscription = this.listenToSelectedRecordUpdate(caseId).subscribe();
    subscribedCaseIds.push(caseId);
    this.subscribedCaseId$.next([...subscribedCaseIds]);

    const caseIdsSubscription = this.caseIdsSubscription$.getValue();
    caseIdsSubscription.push(subscription);
    this.caseIdsSubscription$.next([...caseIdsSubscription]);
  }

  removeCaseIdSubscription() {
    const caseIdsSubscription = this.caseIdsSubscription$.getValue();
    caseIdsSubscription.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  removeImageStoreSubscription() {
    if (this.subscribedImageStoreSuccess$.getValue()) {
      const imageStoreSubs = this.imageStoreSubscription$.getValue();
      if (imageStoreSubs) imageStoreSubs.unsubscribe();
    }
  }

  openViewMoreAdditionalFilesDialog(files) {
    this.dialogRepository.loadDialog(ListAdditionalFilesDialog, {
      files,
      onCrossIconClick: () => {
        this.shouldReopenAdditionalFilesDialog$.next(false);
        this.closeDialog();
      },
      onViewFile: (file) => {
        this.shouldReopenAdditionalFilesDialog$.next(true);
        this.closeDialog();
        this.openViewer(file);
      },
    });
  }

  getResourceByUuid(uuid) {
    return this.resourceRepository.getResourceInfo(uuid);
  }

  checkReopenAdditionalFilesDialog() {
    const shouldReopen = this.shouldReopenAdditionalFilesDialog$.getValue();
    if (!shouldReopen) return;

    const selectedRecord = this.selectedRecord$.getValue();

    this.shouldReopenAdditionalFilesDialog$.next(false);
    this.openViewMoreAdditionalFilesDialog(selectedRecord?.case?.additionalFiles || []);
  }

  async openViewer(material) {
    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.uuid);
        this.dialogRepository.loadDialog(AmbraContainer, {
          onClose: () => {
            this.dialogRepository.toggleDialog(false);
            this.checkReopenAdditionalFilesDialog();
          },
          url: resourceInfo?.viewerLink,
        });
        // eslint-disable-next-line no-empty
      } catch (error) {
        this.errorRepository.error$.next({
          message: error?.message || 'Failed to get resource info!',
          severity: 'error',
        });
      }
    }

    if (material.type === IMAGE) {
      this.dialogRepository.loadDialog(ImageWrapper, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: material.downloadLink,
      });
    }

    if (material.type === THREE) {
      this.dialogRepository.loadDialog(ObjViewer, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: material.downloadLink,
      });
    }

    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, {
        isPlayOnClick: true,
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        url: videoUrl || material.downloadLink,
      });
    }

    if (material.type === DOCS) {
      this.dialogRepository.loadDialog(PdfViewer, {
        onClose: () => {
          this.checkReopenAdditionalFilesDialog();
        },
        title: material?.title || material?.fileName || '',
        url: material?.downloadLink,
      });
    }
  }

  listenToStoreImageUpdate() {
    const userDetails = this.userRepository.userDetails$.getValue();
    const userId = userDetails.id;

    this.subscribedImageStoreSuccess$.next(true);

    return this.notificationsRepository.getSocketConnection$().pipe(
      distinctUntilChanged((a, b) => a === b),
      switchMap((connected) => {
        if (connected) {
          this.notificationsRepository.sendMessage('join-image-upload-room', { userId });
          return this.notificationsRepository.subscribeChannel('store-image-success');
        }

        return EMPTY;
      }),
      tap(async (event) => {
        if (!event?.studyId) {
          this.errorRepository.error$.next({
            message: 'Failed to upload image',
            severity: 'error',
          });
          return;
        }

        this.errorRepository.error$.next({
          message: 'File uploaded successfully!',
          severity: 'success',
        });

        await this.getCurrentUser();
        await this.getRecords();
        await this.getSelectedRecord(event.studyId);
      })
    );
  }

  listenToSelectedRecordUpdate(caseId) {
    return this.notificationsRepository.getSocketConnection$().pipe(
      distinctUntilChanged((a, b) => a === b),
      switchMap((connected) => {
        if (connected) {
          this.notificationsRepository.sendMessage('join-case-details-room', { caseId });
          return this.notificationsRepository.subscribeChannel('additional-files-updated');
        }

        return EMPTY;
      }),
      tap((event) => {
        if (!event?.data?.caseId || !event?.data?.resourceId) return;

        if (event?.data?.success) {
          this.errorRepository.error$.next({
            message: 'Additional files updated!',
            severity: 'success',
          });

          this.updatedAdditionalFile$.next({ caseId: event?.data?.caseId, resourceId: event?.data?.resourceId, success: true });
          return;
        }

        this.errorRepository.error$.next({
          message: 'Failed to update additional file',
          severity: 'error',
        });
        this.updatedAdditionalFile$.next({ caseId: event?.data?.caseId, resourceId: event?.data?.resourceId, success: false });
      })
    );
  }

  async openUpdatedAdditionalFileRecord() {
    const updatedAdditionalFile = this.updatedAdditionalFile$.getValue();
    if (!updatedAdditionalFile?.resourceId) return;

    this.getSelectedRecord(updatedAdditionalFile.resourceId);
    this.updatedAdditionalFile$.next({});
  }

  async closeUpdatedAdditionalFileRecord() {
    this.updatedAdditionalFile$.next({});
  }

  makePayment() {
    this.shouldRedirectToPackagePlans$.next(true);
  }
}
